/**
 * devRequestMonitor — 測試面板的「API 紀錄 / 連點偵測 / 寫入延遲」(只在測試環境生效)
 *
 * 對應新站 SDK 的 dev/scenarios.js(情境測試)。傳統站的 axios instance 是各支 api 檔
 * 各自 createApi 出來的,沒有共用的地方能掛攔截器,所以改在 XMLHttpRequest 這層監看
 * (axios 在瀏覽器走 XHR),不用動 baseApi。
 *
 *   · API 紀錄:每支請求的 method / url / body(密碼類欄位遮掉)/ 狀態碼
 *   · 連點偵測:同一支送出類 API(POST/PUT/PATCH/DELETE)DUP_WINDOW_MS 內又送一次 → 標「⚠ 重複」並計數
 *     (2026-10-12 棄分連續觸發 API 事故;cli-verify 規則 ABANDON-SUBMIT-LOCK)
 *   · 寫入延遲:勾了以後送出類請求晚 3 秒才送(**會真的送出**,只是延後),把連點的空窗拉大
 *
 * 正式站:isTestEnv 為 false 時 installRequestMonitor 什麼都不做。
 * 新站對應:cli-integrated-api/src/dev/scenarios.js、components/ScenarioDevPanel.jsx。
 */

export const DUP_WINDOW_MS = 2000;
const SLOW_WRITE_MS = 3000;
const LOG_MAX = 60;
const STORE_KEY = "wsky_dev_request_monitor";
const WRITE_METHODS = ["POST", "PUT", "PATCH", "DELETE"];
const SECRET_RX = /pwd|password|token|secret/i;

const mem = {
  installed: false,
  slowWrite: false,
  log: [],
  dups: [],
  listeners: new Set(),
};

const emit = () => mem.listeners.forEach((fn) => fn());

try {
  mem.slowWrite =
    JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}").slowWrite === true;
} catch (e) {
  /* sessionStorage 不能用就只放記憶體 */
}

export const subscribeRequestMonitor = (fn) => {
  mem.listeners.add(fn);
  return () => mem.listeners.delete(fn);
};

export const getRequestMonitorState = () => ({
  installed: mem.installed,
  slowWrite: mem.slowWrite,
  log: mem.log,
  dups: mem.dups,
});

export const setSlowWrite = (on) => {
  mem.slowWrite = !!on;
  try {
    sessionStorage.setItem(
      STORE_KEY,
      JSON.stringify({ slowWrite: mem.slowWrite }),
    );
  } catch (e) {
    /* ignore */
  }
  emit();
};

export const clearRequestLog = () => {
  mem.log = [];
  mem.dups = [];
  emit();
};

const mask = (v) => {
  if (!v || typeof v !== "object") return v;
  const out = Array.isArray(v) ? [] : {};
  Object.keys(v).forEach((k) => {
    out[k] = SECRET_RX.test(k) ? "***" : mask(v[k]);
  });
  return out;
};

const parseBody = (body) => {
  if (body == null) return undefined;
  if (typeof FormData !== "undefined" && body instanceof FormData)
    return "[FormData]";
  if (typeof body !== "string") return undefined;
  try {
    return mask(JSON.parse(body));
  } catch (e) {
    return body.length > 200 ? `${body.slice(0, 200)}…` : body;
  }
};

const shortUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname;
  } catch (e) {
    return String(url || "").split("?")[0];
  }
};

/** 掛上 XHR 監看。重複呼叫只會掛一次;enabled 為 false(正式站)時什麼都不做。 */
export const installRequestMonitor = (enabled) => {
  if (!enabled || mem.installed || typeof XMLHttpRequest === "undefined")
    return;
  mem.installed = true;

  const proto = XMLHttpRequest.prototype;
  const origOpen = proto.open;
  const origSend = proto.send;

  proto.open = function (method, url, ...rest) {
    this.__devReq = {
      method: String(method || "GET").toUpperCase(),
      url: shortUrl(url),
    };
    return origOpen.call(this, method, url, ...rest);
  };

  proto.send = function (body) {
    const info = this.__devReq;
    if (!info) return origSend.call(this, body);

    const now = Date.now();
    const entry = {
      t: new Date(now).toLocaleTimeString(),
      ts: now,
      method: info.method,
      url: info.url,
      body: parseBody(body),
      status: null,
    };
    const isWrite = WRITE_METHODS.includes(entry.method);
    if (isWrite) {
      const prev = mem.log.find(
        (e) => e.method === entry.method && e.url === entry.url,
      );
      if (prev && now - prev.ts <= DUP_WINDOW_MS) {
        entry.dup = true;
        mem.dups = [
          { t: entry.t, method: entry.method, url: entry.url },
          ...mem.dups,
        ].slice(0, 20);
        console.warn("[測試面板] 連點重複送出", entry.method, entry.url);
      }
    }
    mem.log = [entry, ...mem.log].slice(0, LOG_MAX);

    this.addEventListener("loadend", () => {
      entry.status = this.status || "ERR";
      emit();
    });
    emit();

    if (mem.slowWrite && isWrite) {
      entry.delayed = true;
      setTimeout(() => {
        try {
          origSend.call(this, body);
        } catch (e) {
          entry.status = "ERR";
          emit();
        }
      }, SLOW_WRITE_MS);
      return undefined;
    }
    return origSend.call(this, body);
  };
};
